import * as React from 'react';

import { tv, type VariantProps } from 'tailwind-variants';

import cn from '../../utils/cn';

const tagVariants = tv({
  base: 'flex h-fit select-none items-center justify-center rounded-2xl border border-transparent px-2.5 py-1 text-xs font-medium',
  variants: {
    variant: {
      default: 'bg-gray/10',
      contained: 'bg-gray-light',
    },
    color: {
      default: 'text-gray-dark',
      primary: 'text-primary',
      success: 'text-success',
      danger: 'text-danger',
      warning: 'text-warning',
    },
  },
  compoundVariants: [
    {
      variant: 'default',
      color: 'default',
      className: 'bg-gray/10',
    },
    {
      variant: 'default',
      color: 'primary',
      className: 'bg-primary/10',
    },
    {
      variant: 'default',
      color: 'success',
      className: 'bg-success/10',
    },
    {
      variant: 'default',
      color: 'danger',
      className: 'bg-danger/10',
    },
    {
      variant: 'default',
      color: 'warning',
      className: 'bg-warning/10',
    },
    {
      variant: 'contained',
      color: 'default',
      className: 'bg-gray-dark text-white',
    },
    {
      variant: 'contained',
      color: 'primary',
      className: 'bg-primary  text-white',
    },
    {
      variant: 'contained',
      color: 'success',
      className: 'bg-success text-white',
    },
    {
      variant: 'contained',
      color: 'danger',
      className: 'bg-danger text-white',
    },
    {
      variant: 'contained',
      color: 'warning',
      className: 'bg-warning text-white',
    },
  ],
  defaultVariants: {
    color: 'default',
    variant: 'default',
  },
});

const customIcon = (icon: React.ReactNode) => {
  return React.cloneElement(icon as React.ReactElement, {
    'data-testid': 'tag-icon',
    style: { width: '0.75rem', height: '0.75rem' }, // 12px
  });
};

type DefaultProps = {
  label: string;
  icon?: React.ReactNode;
  className?: string;
  color?: 'default' | 'primary' | 'success' | 'danger' | 'warning';
};

type IconProps = {
  icon: React.ReactNode;
  label?: string;
  color?: 'default' | 'primary' | 'success' | 'danger' | 'warning';
};

export type BaseButtonProps = React.ButtonHTMLAttributes<HTMLDivElement> &
  VariantProps<typeof tagVariants>;

export type TagProps = BaseButtonProps & (IconProps | DefaultProps);

const Tag = React.forwardRef<HTMLDivElement, TagProps>(
  ({ className, variant, color, label, icon, ...props }, ref) => {
    return (
      <div
        className={cn(tagVariants({ variant, className, color }), 'gap-2.5')}
        ref={ref}
        {...props}
      >
        {icon ? customIcon(icon) : null}
        {label}
      </div>
    );
  }
);

Tag.displayName = 'Tag';

export default Tag;
